import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { LedgerService, RmsMessageService, RmsPulldownService } from '@services';
import { JournalFormComponent } from '../journal-form/journal-form.component';
import { dateOnly, exportLedgerExcel } from '../ledger-export';

@Component({
  selector: 'app-journal-review',
  imports: [RmsModule],
  templateUrl: './journal-review.component.html',
  styleUrl: './journal-review.component.scss'
})
export class JournalReviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() canPrepare = false;

  StatusOptions = [
    { value: '', label: 'All' },
    { value: 'Draft', label: 'Draft' },
    { value: 'In review', label: 'In review' },
    { value: 'Posted', label: 'Posted' },
    { value: 'Canceled', label: 'Canceled' }
  ];
  PeriodOptions: { value: any; label: string }[] = [{ value: '', label: 'All' }];
  Filters = { Status: '', PeriodID: '', SearchText: '' };

  Rows: any[] = [];
  Loading = false;
  private request = 0;

  Columns: Column[] = [
    { label: 'Journal', key: 'JournalNumber', width: 8 },
    { label: 'Status', key: 'Status', width: 8, SearchType: 'select' },
    { label: 'Posting Date', key: 'EntryDate', type: 'date', width: 9 },
    { label: 'Period', key: 'PeriodName', width: 7 },
    { label: 'Reason', key: 'Reason', width: 18 },
    { label: 'Source Reference', key: 'SourceReference', width: 14 },
    { label: 'Lines', key: 'LineCount', type: 'number', width: 5, align: 'right' },
    { label: 'Debits', key: 'DebitTotal', type: 'currency', width: 9, align: 'right' },
    { label: 'Balanced', key: 'BalancedDisplay', width: 7 },
    { label: 'Prepared By', key: 'PreparedByName', width: 12 }
  ];

  Buttons: HeaderButton[] = this.buildButtons();

  ngOnChanges() {
    this.Buttons = this.buildButtons();
  }

  private buildButtons(): HeaderButton[] {
    return [
      { label: 'New journal', icon: 'add', class: 'btn-bg-green', disabled: !this.canPrepare, onAction: () => this.canPrepare && this.open(null) },
      { label: 'Export', icon: 'download', class: 'btn-bg-blue', onAction: () => this.export() }
    ];
  }

  constructor(private ledger: LedgerService, private message: RmsMessageService, private pulldown: RmsPulldownService) { }

  ngOnInit() {
    this.ledger.getPeriods().subscribe({
      next: (periods) => this.PeriodOptions = [{ value: '', label: 'All' }, ...(periods || []).map((p: any) => ({ value: p.PeriodID, label: `${p.PeriodName} (${p.Status})` }))],
      error: (err) => this.message.error('Failed to load periods: ' + this.errorText(err))
    });
    this.load();
  }

  ngOnDestroy() { ++this.request; }

  load() {
    const request = ++this.request;
    this.Loading = true;
    this.ledger.getJournals(this.Filters).subscribe({
      next: (rows) => {
        if (request !== this.request) return;
        this.Loading = false;
        this.Rows = (rows || []).map((r: any) => ({ ...r, BalancedDisplay: r.IsBalanced ? 'Yes' : 'No' }));
      },
      error: (err) => {
        if (request !== this.request) return;
        this.Loading = false;
        this.message.error('Failed to load journals: ' + this.errorText(err));
      }
    });
  }

  open(row: any) {
    const ref = this.pulldown.open(JournalFormComponent, {
      title: row ? `Journal ${row.JournalNumber}` : 'New journal',
      data: { JournalEntryID: row?.JournalEntryID ?? null },
      width: '85vw',
      maxWidth: '95vw',
      maxHeight: '92vh',
      top: '0'
    });
    ref.afterClosed().subscribe(() => this.load());
  }

  get FilterSummary(): string {
    const period = this.PeriodOptions.find(p => String(p.value) === String(this.Filters.PeriodID))?.label;
    return [
      this.Filters.Status ? 'Status: ' + this.Filters.Status : '',
      this.Filters.PeriodID ? 'Period: ' + period : '',
      this.Filters.SearchText ? 'Search: ' + this.Filters.SearchText : ''
    ].filter(Boolean).join(' | ') || 'All journals';
  }

  async export() {
    if (!this.Rows.length) { this.message.warn('There are no journals to export.'); return; }
    await exportLedgerExcel({
      title: 'Journal Review',
      fileName: 'journals',
      filterSummary: this.FilterSummary,
      summary: [['Journals', this.Rows.length]],
      columns: [
        { header: 'Journal', value: r => r.JournalNumber, width: 10 },
        { header: 'Status', value: r => r.Status, width: 11 },
        { header: 'Posting Date', value: r => dateOnly(r.EntryDate), width: 12 },
        { header: 'Period', value: r => r.PeriodName, width: 10 },
        { header: 'Reason', value: r => r.Reason, width: 36 },
        { header: 'Source Reference', value: r => r.SourceReference, width: 26 },
        { header: 'Source Document ID', value: r => r.SourceDocumentID, width: 18 },
        { header: 'External Reference', value: r => r.ExternalReference, width: 18 },
        { header: 'Payment Method', value: r => r.PaymentMethodName, width: 16 },
        { header: 'Check #', value: r => r.CheckNumber, width: 12 },
        { header: 'Lines', value: r => r.LineCount, width: 8 },
        { header: 'Debits', value: r => r.DebitTotal, width: 14, money: true },
        { header: 'Credits', value: r => r.CreditTotal, width: 14, money: true },
        { header: 'Balanced', value: r => r.IsBalanced ? 'Yes' : 'No', width: 10 },
        { header: 'Prepared By', value: r => r.PreparedByName, width: 20 },
        { header: 'Approved / Posted By', value: r => r.PostedByName, width: 20 },
        { header: 'Posted On', value: r => r.PostedDate ? new Date(r.PostedDate).toLocaleString() : '', width: 20 },
        { header: 'Reverses', value: r => r.ReversesJournalNumber, width: 10 },
        { header: 'Reversed By', value: r => r.ReversedByJournalNumber, width: 12 },
        { header: 'Canceled Reason', value: r => r.CancelReason, width: 28 }
      ],
      rows: this.Rows
    });
  }

  private errorText(err: any): string {
    return err?.error?.error || err?.message || 'Unknown error';
  }
}
